/**
 * フォームプレビュー（編集画面のサイドペイン）。
 * 公開フォームと同じレイアウトでレンダリングするが、disabled で操作不可。
 *
 * 仕様書: docs/01_spec/spec_line_bot.md A-12.4（Phase 24b）
 *
 * 動的フィールド (product_picker / briefing_schedule_picker / assignee_picker) は、
 * editor 認証経由で getFormDynamicOptionsForPreview Callable を呼び、
 * 公開フォームと同じデータを取得して表示する。
 */
import { httpsCallable } from 'firebase/functions';
import { Eye } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { Form, PublicFormDynamicOptions } from '@goenhub/shared';

import { functions } from '@/lib/firebase';

import { FormElementRenderer } from './FormElementRenderer';

interface Props {
  form: Form;
  /** 編集中フォームの doc id。新規作成時は undefined。 */
  formId?: string;
}

const DYNAMIC_FIELD_TYPES = new Set([
  'product_picker',
  'briefing_schedule_picker',
  'assignee_picker',
]);

const fetchDynamicOptionsFn = httpsCallable<
  { formId: string },
  { ok: boolean; dynamicOptions?: PublicFormDynamicOptions }
>(functions, 'getFormDynamicOptionsForPreview');

export function FormPreview({ form, formId }: Props) {
  const [values, setValues] = useState<Record<string, string | string[]>>({});
  const [dynamicOptions, setDynamicOptions] = useState<
    PublicFormDynamicOptions | undefined
  >(undefined);
  const brandColor = form.theme?.brandColor;
  const bgColor = form.theme?.backgroundColor || undefined;
  const elements = Array.isArray(form.elements) ? form.elements : [];

  const hasDynamicField = useMemo(
    () =>
      elements.some(
        (el) =>
          el.kind === 'field' && DYNAMIC_FIELD_TYPES.has(el.fieldType),
      ),
    [elements],
  );

  useEffect(() => {
    if (!formId || !hasDynamicField) {
      setDynamicOptions(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchDynamicOptionsFn({ formId });
        if (!cancelled) setDynamicOptions(r.data.dynamicOptions);
      } catch {
        if (!cancelled) setDynamicOptions(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
    // form.primaryCategoryId が変わったら再取得
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, hasDynamicField, form.primaryCategoryId]);

  const handleChange = (id: string, v: string | string[]) => {
    setValues((s) => ({ ...s, [id]: v }));
  };

  return (
    <div className="rounded border border-[var(--border)] bg-[var(--bg-elevated)] overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-hover)] border-b border-[var(--border)] text-[10px] text-[var(--text-muted)]">
        <Eye className="size-3" />
        プレビュー（操作はインタラクティブ、送信はされません）
      </div>
      <div
        className="bg-white text-gray-900 overflow-y-auto"
        style={{ maxHeight: 'calc(100vh - 280px)', backgroundColor: bgColor, colorScheme: 'light' }}
      >
        <div className="max-w-md mx-auto p-6 space-y-4">
          {form.theme?.logoUrl && (
            <img
              src={form.theme.logoUrl}
              alt=""
              className="max-h-16 mx-auto"
            />
          )}
          {form.title && (
            <h1
              className="text-xl font-bold text-center whitespace-pre-line"
              style={brandColor ? { color: brandColor } : undefined}
            >
              {form.title}
            </h1>
          )}
          {form.description && (
            <p className="text-[13px] text-gray-600 text-center whitespace-pre-line">
              {form.description}
            </p>
          )}

          <div className="space-y-4 pt-2">
            {elements.length === 0 ? (
              <p className="text-xs text-gray-400 italic text-center py-8">
                要素未追加。左側で要素を追加するとここに表示されます。
              </p>
            ) : (
              elements.map((el) => (
                <FormElementRenderer
                  key={el.id}
                  element={el}
                  values={values}
                  onChange={handleChange}
                  brandColor={brandColor}
                  dynamicOptions={dynamicOptions}
                />
              ))
            )}
          </div>

          <button
            type="button"
            disabled
            className="w-full mt-4 py-2.5 rounded-md text-white font-semibold text-[14px] opacity-90 cursor-not-allowed"
            style={{ backgroundColor: brandColor ?? '#4a7cff' }}
          >
            送信（プレビューでは無効）
          </button>
        </div>
      </div>
    </div>
  );
}
